import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { IoIosRemoveCircleOutline } from "react-icons/io";
// import { IoMdPaperPlane } from "react-icons/io";
import { LiaShippingFastSolid } from "react-icons/lia";
import useRecommendedLimit from "../libs/fetchLimitRecommended";
import MarketNavbar from "../MarketNav/MarketNavbar";
import MarketplacePage from "../components/MarketplacePage";
import MarketProductSection from "../components/MarketProductSection";
import { MARKET_HOME_SECTIONS } from "../libs/marketSectionMeta";
import { CartContext } from "../../../Context/CartContext";
import { ReactComponent as CautionIcon } from "../../../Assets/caution-svgrepo-com 1.svg";
import "react-loading-skeleton/dist/skeleton.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-loading-skeleton/dist/skeleton.css";
import { AuthContext } from "../../../Context/AuthContext";
import CheckoutAuthStrip from "../components/CheckoutAuthStrip";
import { syncCartToServer } from "../libs/cartSyncApi";
import "./CartPayment/checkout.css";
import { cartLineKey } from "../libs/productSizes";
import { refreshCartStockLevels, recoverFromStockError } from "../libs/validateCartStock";
import { getCartStockBannerText } from "../libs/cartStockMessages";

const CartListView = () => {
  const { data: recommendedLimit, isLoading: recommendedLoading, isError: recommendedError } = useRecommendedLimit();
  const [removingOverlay, setRemovingOverlay] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);
  const [stockBanner, setStockBanner] = useState(null);
  const [checkingStock, setCheckingStock] = useState(false);
  const {
    cartItems,
    removeItemFromCart,
    clearItemFromCart,
    addItemToCart,
    replaceCartItems,
    totalCart,
  } = useContext(CartContext);
  const { isLoggedIn, user, token } = useContext(AuthContext);
  const navigate = useNavigate();

  const notify = () => toast.success("Added to cart");
  const notifies = () => toast.success("Removed from cart");

  useEffect(() => {
    if (cartItems.length === 0) return;

    let cancelled = false;

    refreshCartStockLevels(cartItems, replaceCartItems)
      .then((result) => {
        if (cancelled) return;
        if (result.hadChanges) {
          result.messages.forEach((msg) => toast.warn(msg, { autoClose: 8000 }));
          setStockBanner(getCartStockBannerText(result));
        }
        if (result.items.length === 0) {
          navigate("/market/cart/empty");
        }
      })
      .catch((err) => {
        if (!cancelled && !err?.response) {
          toast.warn("Stock levels may be outdated. We'll recheck when you checkout.");
        } else if (!cancelled) {
          toast.error("Could not check stock levels. Try refreshing the page.");
        }
      });

    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (cartItems.length === 0) {
      navigate("/market/cart/empty");
    }
  }, [cartItems, navigate]);

  const handleDecreaseItem = (cartItem) => {
    removeItemFromCart(cartItem);
    notifies();
  };

  const handleRemoveItem = () => {
    clearItemFromCart(itemToRemove); 
    setRemovingOverlay(false);
  };

  const handleIncreaseCart = (cartItem) => {
    const result = addItemToCart(cartItem);
    if (result.success) {
      notify();
    } else {
      toast.error(result.message || "Cannot add more of this item.");
    }
  };

  const handleCheckOut = async () => {
    setCheckingStock(true);
    setStockBanner(null);
    let stockCheck;
    try {
      stockCheck = await refreshCartStockLevels(cartItems, replaceCartItems);
    } catch (err) {
      toast.error("Could not verify stock. Please try again.");
      setCheckingStock(false);
      return;
    }

    if (stockCheck.hadChanges) {
      setStockBanner(getCartStockBannerText(stockCheck));
      stockCheck.messages.forEach((msg) => toast.warn(msg, { autoClose: 8000 }));
    }

    if (!stockCheck.ok || stockCheck.items.length === 0) {
      toast.error(
        stockCheck.messages?.[0] ||
          "Every item in your cart is sold out. Add in-stock products to checkout."
      );
      setCheckingStock(false);
      return;
    }

    if (stockCheck.hadChanges) {
      toast.info("Your cart was updated. Review the items below, then tap Checkout again.");
      setCheckingStock(false);
      return;
    }

    if (isLoggedIn && user?.role === "customer") {
      try {
        await syncCartToServer(stockCheck.items, token);
      } catch (err) {
        const { message } = await recoverFromStockError(
          err,
          stockCheck.items,
          replaceCartItems
        );
        setStockBanner(message);
        toast.error(message, { autoClose: 10000 });
        setCheckingStock(false);
        return;
      }
    }

    setCheckingStock(false);
    navigate("/market/checkout");
  };

  const handleRemovingOverlay = (cartItem) => {
    setItemToRemove(cartItem); 
    setRemovingOverlay(true);
  };

  return (
    <>
      <MarketNavbar />
      <MarketplacePage className="marketplace-page--cart">
      <div className="cart-list-container">
        {stockBanner && (
          <div className="cart-stock-banner" role="alert">
            <strong>Stock update</strong>
            <p>{stockBanner}</p>
          </div>
        )}
        {cartItems.length > 0 && (
          <div className="cart-page">
            <div className="cart-page__main">
              <h1 className="cart-page__title">
                Shopping bag ({cartItems.length})
              </h1>
              <div className="cart-page__items">
            {cartItems.map((cartItem) => (
              <div className="cart-check" key={cartLineKey(cartItem)}>
                <div className="cart-con-1">
                  <div className="cart-span-1">
                    {cartItem.images && cartItem.images.length > 0 && (
                      <img src={cartItem.images[0]} alt={cartItem.description} />
                    )}
                    {removingOverlay && cartLineKey(itemToRemove) === cartLineKey(cartItem) && ( 
                      <div className="removal-overlay">
                        <div className="removing-card">
                          <CautionIcon />
                          <h3>Delete</h3>
                          <p>Are you sure you want to remove this item from cart?</p>
                          <div className="btn">
                            <button onClick={() => setRemovingOverlay(false)}>No</button>
                            <button onClick={handleRemoveItem}>Yes</button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="cart-span-2">
                    <h3>{cartItem.name}</h3>
                    {cartItem.selectedSize && (
                      <p className="cart-item-size">Size: {cartItem.selectedSize}</p>
                    )}
                    <p>
                      {cartItem.quantity && cartItem.price
                        ? (cartItem.quantity * cartItem.price).toLocaleString("en-US", {
                            style: "currency",
                            currency: "NGN",
                          })
                        : 0}
                    </p>
                    <h4>
                      {/* Smart <IoMdPaperPlane style={{ color: " #d0300e " }} /> */}
                      Smart <LiaShippingFastSolid style={{ color: " #d0300e " }} />
                      <span>Express</span>
                    </h4>
                  </div>
                </div>
                <div className="cart-con-2">
                  <span>
                    <h3>Quantity</h3>
                    <div className="add-remove-button">
                      <button onClick={() => handleDecreaseItem(cartItem)}>
                        <i className="fa-solid fa-minus"></i>
                      </button>
                      <p> {cartItem.quantity}</p>
                      <button onClick={() => handleIncreaseCart(cartItem)}>
                        <i className="fa-solid fa-plus"></i>
                      </button>
                    </div>
                  </span>
                  <button onClick={() => handleRemovingOverlay(cartItem)}>
                    <IoIosRemoveCircleOutline style={{ fontSize: "30px" }} />
                  </button>
                </div>
              </div>
            ))}
              </div>
            </div>
            <aside className="cart-summary">
              <h2 className="cart-summary__title">Order summary</h2>
              <div className="cart-summary__row">
                <span>Subtotal</span>
                <strong>
                  {totalCart
                    ? totalCart.toLocaleString("en-US", {
                        style: "currency",
                        currency: "NGN",
                      })
                    : "—"}
                </strong>
              </div>
              <p className="cart-summary__note">
                Shipping and taxes calculated at checkout
              </p>
              <button
                type="button"
                className="cart-summary__cta"
                onClick={handleCheckOut}
                disabled={checkingStock}
              >
                {checkingStock ? "Checking stock…" : "Checkout"}
              </button>
              {!isLoggedIn && (
                <CheckoutAuthStrip
                  returnUrl="/market/checkout"
                  className="checkout-auth-strip--summary"
                />
              )}
            </aside>
          </div>
        )}
      </div>

      <MarketProductSection
        title={MARKET_HOME_SECTIONS.recommended.title}
        Icon={MARKET_HOME_SECTIONS.recommended.Icon}
        viewAllLink={MARKET_HOME_SECTIONS.recommended.link}
        isLoading={recommendedLoading}
        isError={recommendedError}
        products={recommendedLimit?.products || []}
        errorMessage={MARKET_HOME_SECTIONS.recommended.errorMessage}
        emptyMessage={MARKET_HOME_SECTIONS.recommended.emptyMessage}
        limit={5}
      />
      </MarketplacePage>
      <ToastContainer position="top-right" className="toast" />
    </>
  );
};

export default CartListView;

